import { useEffect, useState } from "react";

// Cloudflare Worker in workers/live: it counts open "listening" sockets and pushes the total on every change.
const URL = "wss://live.lofi.cafe/ws";
const PING_MS = 60_000;
// The Worker answers every ping, so a minute of silence means the connection died while the device slept.
const SILENCE_LIMIT_MS = PING_MS + 10_000;
const RETRY_MS = [2_000, 5_000, 15_000, 60_000];
const LISTENING_SETTLE_MS = 4_000;
const KEEP_COUNT_MS = 15_000;

export default function useListenerPresence(isListening) {
  const [count, setCount] = useState(null);
  // Buffering and station changes flip listening constantly; only switch sockets once it settles.
  const [listening, setListening] = useState(isListening);

  useEffect(() => {
    const timeout = setTimeout(() => setListening(isListening), LISTENING_SETTLE_MS);
    return () => clearTimeout(timeout);
  }, [isListening]);

  useEffect(() => {
    let socket;
    let lastMessageAt;
    let failures = 0;
    let retryTimeout;
    let hideTimeout;

    function connect() {
      clearTimeout(retryTimeout);
      lastMessageAt = Date.now();
      socket = new WebSocket(`${URL}?listening=${listening ? 1 : 0}`);
      socket.onopen = () => {
        failures = 0;
      };
      socket.onmessage = ({ data }) => {
        lastMessageAt = Date.now();
        const nextCount = Number(data);
        if (!Number.isSafeInteger(nextCount) || nextCount < 0) return; // "pong"
        clearTimeout(hideTimeout);
        hideTimeout = undefined;
        setCount(nextCount);
      };
      socket.onclose = () => {
        // Network blips and brief outages reconnect within seconds; keep the badge steady meanwhile.
        hideTimeout ??= setTimeout(() => setCount(null), KEEP_COUNT_MS);
        const delay = RETRY_MS[Math.min(failures++, RETRY_MS.length - 1)];
        // Jitter spreads reconnects when an outage drops every socket at once.
        retryTimeout = setTimeout(connect, delay * (0.5 + Math.random()));
      };
    }

    function disconnect() {
      socket.onclose = null;
      socket.close();
    }

    // Sleeping laptops and backgrounded phones drop sockets, sometimes without a close event.
    function checkConnection() {
      if (socket.readyState === WebSocket.CLOSED) {
        connect();
      } else if (socket.readyState === WebSocket.OPEN && Date.now() - lastMessageAt > SILENCE_LIMIT_MS) {
        disconnect();
        connect();
      }
    }

    function onVisible() {
      if (document.visibilityState === "visible") checkConnection();
    }

    connect();
    const pingInterval = setInterval(() => {
      checkConnection();
      if (socket.readyState === WebSocket.OPEN) socket.send("ping");
    }, PING_MS);
    window.addEventListener("online", checkConnection);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(pingInterval);
      clearTimeout(retryTimeout);
      clearTimeout(hideTimeout);
      window.removeEventListener("online", checkConnection);
      document.removeEventListener("visibilitychange", onVisible);
      disconnect();
    };
  }, [listening]);

  return count;
}
